import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { familyApi } from '../services/family';
import '../styles/forms.css';
import '../styles/FamilyPage.css';

const AcceptInvite = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);

  const isAuthenticated = !!localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided.');
      setLoading(false);
      return;
    }
    // Use the public (unauthenticated) lookup so the invitation can be
    // shown before the user has signed up.
    familyApi
      .publicLookupInvitation(token)
      .then((data) => setInvite(data))
      .catch((err) => setError(err.response?.data?.error || 'Invitation not found.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    // Not logged in: go sign up carrying the token. The backend auto-accepts
    // the invitation during signup, so the user doesn't have to come back
    // here afterwards.
    if (!isAuthenticated) {
      navigate(`/signup?invite_token=${encodeURIComponent(token)}`);
      return;
    }
    // Already authenticated: accept immediately.
    setActing(true);
    setError('');
    try {
      await familyApi.acceptInvitation(token);
      const remaining = (JSON.parse(localStorage.getItem('pendingFamilyInvitations') || '[]'))
        .filter((i) => i.token !== token);
      localStorage.setItem('pendingFamilyInvitations', JSON.stringify(remaining));
      localStorage.setItem('invitationAccepted', 'true');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invitation.');
    } finally {
      setActing(false);
    }
  };

  if (loading) return <div className="card"><p className="family-hint">Loading invitation…</p></div>;

  if (!token || (!invite && error)) {
    return (
      <div className="card">
        <h2 className="page-title">Accept Invitation</h2>
        <div className="error">{error || 'Invitation not found.'}</div>
        <div className="form-buttons">
          <button className="btn btn--secondary" onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  if (invite.status === 'confirmed') {
    return (
      <div className="card">
        <h2 className="page-title">Already a Member</h2>
        <p className="family-hint">You are already a confirmed member of <strong>{invite.family_name}</strong>.</p>
        <div className="form-buttons">
          <button className="btn btn--primary" onClick={() => navigate('/')}>Go to Home</button>
        </div>
      </div>
    );
  }

  if (invite.status === 'accepted') {
    return (
      <div className="card">
        <h2 className="page-title">Awaiting Confirmation</h2>
        <p className="family-hint">
          You have accepted the invitation to join <strong>{invite.family_name}</strong>.
          The family admin will now confirm your membership and assign your accounts.
        </p>
        <div className="form-buttons">
          <button className="btn btn--secondary" onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  if (invite.status === 'revoked') {
    return (
      <div className="card">
        <h2 className="page-title">Invitation Revoked</h2>
        <p className="family-hint">This invitation has been revoked by the family admin.</p>
        <div className="form-buttons">
          <button className="btn btn--secondary" onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  const expired = invite.expired || (invite.expires_at && new Date(invite.expires_at) < new Date());

  return (
    <div className="card">
      <h2 className="page-title">Join Family</h2>
      <p className="family-hint">
        You have been invited to join <strong>{invite.family_name}</strong> as a{' '}
        <strong>{invite.proposed_role === 'admin' ? 'Admin' : 'Member'}</strong>.
      </p>
      {expired && <div className="error">This invitation has expired.</div>}
      {error && <div className="error">{error}</div>}
      <p className="family-hint">
        {invite.proposed_role === 'admin'
          ? 'As an Admin you will have access to all accounts and can manage the family.'
          : 'As a Member you will have access to a selection of accounts chosen by the admin.'}
      </p>
      {!isAuthenticated && !expired && (
        <p className="family-hint">
          {acting ? 'Redirecting…' : 'To accept, you need a MoneyLens account. We will set one up in a moment.'}
        </p>
      )}
      <div className="form-buttons">
        <button className="btn btn--secondary" onClick={() => navigate('/')} disabled={acting}>
          Decline
        </button>
        <button className="btn btn--primary" onClick={handleAccept} disabled={acting || expired}>
          {acting ? 'Accepting…' : (isAuthenticated ? 'Accept Invitation' : 'Accept & Sign Up')}
        </button>
      </div>
    </div>
  );
};

export default AcceptInvite;